"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState, type ComponentType } from "react";
import ChartSkeleton from "@/components/pages/charts/ChartSkeleton";

/** lg slot: chart header (~50px) + 360px plot + panel padding */
const CHART_HEIGHT = { lg: "h-[430px]", md: "h-[240px]" } as const;

let mountChain: Promise<void> = Promise.resolve();

function scheduleIdleMount(run: () => void): void {
  mountChain = mountChain.then(
    () =>
      new Promise((resolve) => {
        if (typeof requestIdleCallback === "function") {
          requestIdleCallback(
            () => {
              run();
              resolve();
            },
            { timeout: 3000 },
          );
        } else {
          window.setTimeout(() => {
            run();
            resolve();
          }, 16);
        }
      }),
  );
}

function CyphChartSlot({
  load,
  size,
  className = "",
}: {
  load: () => Promise<{ default: ComponentType }>;
  size: keyof typeof CHART_HEIGHT;
  className?: string;
}): React.ReactElement {
  const hostRef = useRef<HTMLDivElement>(null);
  const [show, setShow] = useState(false);
  const Chart = useMemo(() => dynamic(load, { ssr: false }), [load]);

  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;

    let cancelled = false;

    const mountChart = () => {
      if (cancelled) return;
      scheduleIdleMount(() => {
        if (!cancelled) setShow(true);
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        mountChart();
      },
      { rootMargin: "0px 0px 200px 0px" },
    );

    observer.observe(el);

    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, []);

  return (
    <div ref={hostRef} className={`relative w-full ${CHART_HEIGHT[size]} flex flex-col min-h-0`}>
      {show ? (
        <Chart />
      ) : (
        <div className="flex-1 min-h-0 w-full overflow-hidden">
          <ChartSkeleton hideHeader fill />
        </div>
      )}
    </div>
  );
}

export function CypherpunkBubbleChartDeferred(): React.ReactElement {
  return (
    <CyphChartSlot
      size="lg"
      className="cyph-bubble-chart-host"
      load={() =>
        import("./CypherpunkTrackerCharts").then((mod) => ({
          default: mod.CypherpunkBubbleChart,
        }))
      }
    />
  );
}

export function CypherpunkCumulativeChartDeferred(): React.ReactElement {
  return (
    <CyphChartSlot
      size="md"
      load={() =>
        import("./CypherpunkTrackerCharts").then((mod) => ({
          default: mod.CypherpunkCumulativeChart,
        }))
      }
    />
  );
}

export function CypherpunkNetworkChartDeferred(): React.ReactElement {
  return (
    <CyphChartSlot
      size="md"
      load={() =>
        import("./CypherpunkTrackerCharts").then((mod) => ({
          default: mod.CypherpunkNetworkChart,
        }))
      }
    />
  );
}
